import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import DoneOutlineIcon from '@mui/icons-material/DoneOutline';
import DangerousIcon from '@mui/icons-material/Dangerous';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RemoveIcon from '@mui/icons-material/Remove';
import PauseIcon from '@mui/icons-material/Pause';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';

//MRT Imports
import {
  MaterialReactTable,
  MRT_CopyButton,
  MRT_ToggleDensePaddingButton,
  MRT_ToggleFiltersButton,
  MRT_ToggleFullScreenButton,
  MRT_ToggleGlobalFilterButton,
  useMaterialReactTable,
} from 'material-react-table';
import { MRT_Localization_FR } from 'material-react-table/locales/fr';

//Material UI Imports
import {
  Avatar,
  Box,
  Button,
  Chip,
  IconButton,
  LinearProgress,
  Menu,
  MenuItem,
  Skeleton,
  Typography
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { ClearIcon } from '@mui/x-date-pickers';
import { useOrganizationSelect } from '../../../context/OrganizationSelectContext';
import { useSelector } from 'react-redux';

import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { mkConfig, generateCsv, download } from 'export-to-csv';
import PageHeader from '../../../components/common/PageHeader';

const csvConfig = mkConfig({
  fieldSeparator: ',',
  decimalSeparator: '.',
  useKeysAsHeaders: true,

});

const O365Users = () => {

  const [filteredO365Users, setfilteredO365Users] = useState([]);
  const { data: o365Users = [], loading, error } = useSelector((state) => state.o365.users);

  const { selectedOrganization } = useOrganizationSelect();

  const exportColumns = [
    'displayName',
    'userPrincipalName',
    'organizationName',
    'assignedLicenses',
    'mailboxStorageUsed',
    'mailboxStorageLimit',
    'created',
    'vboDetails',
    'status'
  ];

  const csvData = filteredO365Users.map(user => ({
    displayName: user.displayName,
    userPrincipalName: user.userPrincipalName,
    mailboxStorageUsed: user.mailboxStorageUsed
      ? (user.mailboxStorageUsed / (1024 ** 3)).toFixed(2)
      : 'N/A',
    mailboxStorageLimit: user.mailboxStorageLimit
      ? (user.mailboxStorageLimit / (1024 ** 3)).toFixed(2)
      : 'N/A',
    organizationName: user.organizationName,
    assignedLicenses: Array.isArray(user.assignedLicenses)
      ? user.assignedLicenses.map(lic => lic.skuLicense).join('-')
      : '',
    created: user.created ? new Date(user.created).toLocaleDateString() : 'N/A',
    vbo365: (() => {
      const lastBackupDate = user.vboDetails?.[0]?.lastBackupDate;
      if (lastBackupDate) {
        const today = new Date().toISOString().split('T')[0];
        return lastBackupDate.startsWith(today) ? 'Actif' : 'Désactivé';
      }
      return 'Inéxistant';
    })(),
    status: user.status,
  }));

  useEffect(() => {
    if (selectedOrganization) {
      setfilteredO365Users(o365Users.filter(
        (item) => item.organizationName === selectedOrganization
      ));
    } else {
      setfilteredO365Users(o365Users);
    }
  }, [selectedOrganization, o365Users]);

  const handleExportRows = (rows) => {
    const rowData = rows.map((row) => row.original);
    const formattedRows = rowData.map(user => ({
      displayName: user.displayName,
      userPrincipalName: user.userPrincipalName,
      organizationName: user.organizationName,
      mailboxStorageUsed: user.mailboxStorageUsed
        ? (user.mailboxStorageUsed / (1024 ** 3)).toFixed(2)
        : 'N/A',
      mailboxStorageLimit: user.mailboxStorageLimit
        ? (user.mailboxStorageLimit / (1024 ** 3)).toFixed(2)
        : 'N/A',
      assignedLicenses: Array.isArray(user.assignedLicenses)
        ? user.assignedLicenses.map(lic => lic.skuLicense).join('-')
        : '',
      created: user.created ? new Date(user.created).toLocaleDateString() : 'N/A',
      vbo365: (() => {
        const lastBackupDate = user.vboDetails?.[0]?.lastBackupDate;
        if (lastBackupDate) {
          const today = new Date().toISOString().split('T')[0];
          return lastBackupDate.startsWith(today) ? 'Actif' : 'Désactivé';
        }
        return 'Inéxistant';
      })(),
      status: user.status,
    }));
    const csv = generateCsv(csvConfig)(formattedRows);
    download(csvConfig)(csv);
  };

  const handleExportData = () => {
    const csv = generateCsv(csvConfig)(csvData);
    download(csvConfig)(csv);
  };

  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const columns = useMemo(
    () => [
      {
        id: 'users', //id used to define `group` column
        columns: [
          {
            accessorFn: (row) => row.displayName, //accessorFn used to join multiple data into a single cell
            id: 'name', //id is still required when using accessorFn instead of accessorKey
            header: 'Nom',
            size: 250,
            Cell: ({ renderedCellValue, row }) => (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '1rem',
                }}
              >{renderedCellValue}
              </Box>
            ),
            filterVariant: 'autocomplete',
          },
          {
            accessorKey: 'userPrincipalName', //hey a simple column for once
            header: 'Email',
          },
          {
            accessorKey: 'organizationName', //hey a simple column for once
            header: 'Client',
            filterVariant: 'autocomplete',
          },
          {
            accessorFn: (row) => Array.isArray(row.assignedLicenses) && row.assignedLicenses.length > 0
              ? row.assignedLicenses.map((license) => license.skuLicense).join('\n')
              : "",
            id: 'licenses',
            header: 'Licences',
            size: 250,
            Cell: ({ row }) => (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  flexWrap: 'wrap',
                }}
              >
                {Array.isArray(row.original.assignedLicenses) && row.original.assignedLicenses.length > 0
                  ? row.original.assignedLicenses.map((license, idx) => (
                    <Chip
                      key={idx}
                      label={license.skuLicense}
                      size="small"
                      variant="outlined"
                      sx={{ mb: 0.5 }}
                    />
                  ))
                  : <Typography variant="body2" color="textSecondary">Aucune</Typography>
                }
              </Box>
            ),
            filterVariant: 'autocomplete',
          },
          {
            accessorFn: (row) => row.created,
            id: 'created',
            header: 'Date de création',
            size: 100,
            Cell: ({ renderedCellValue }) => (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                }}
              >
                {renderedCellValue ? new Date(renderedCellValue).toLocaleDateString() : 'N/A'}
              </Box>
            ),
            filterVariant: 'autocomplete',
          },


          {
            accessorFn: (row) =>
              row.mailboxStorageUsed
                ? (row.mailboxStorageUsed / (1024 ** 3)).toFixed(2)
                : 'N/A',
            id: 'mailboxStorageUsed',
            header: 'Utilisation Mailbox (Go)',
            size: 160,
            Cell: ({ row }) => {
              const used = row.original.mailboxStorageUsed || 0;
              const limit = row.original.mailboxStorageLimit || 0;
              if (!row.original.mailboxStorageUsed || !row.original.mailboxStorageLimit) {
                return (
                  <Typography variant="caption" color="textSecondary">
                    N/A
                  </Typography>
                );
              }
              const usedGB = (used / (1024 ** 3)).toFixed(2);
              const limitGB = (limit / (1024 ** 3)).toFixed(2);
              const percent = Math.min((used / limit) * 100, 100);
              return (
                <Box sx={{ minWidth: 120, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box sx={{ flex: 1 }}>
                    <LinearProgress
                      variant="determinate"
                      value={percent}
                      sx={{ height: 8, borderRadius: 5 }}
                      color={percent > 90 ? 'error' : percent > 70 ? 'warning' : 'primary'}
                    />
                  </Box>
                  <Box sx={{ minWidth: 60 }}>
                    <Typography variant="caption" color="textSecondary">
                      {usedGB} / {limitGB} Go
                    </Typography>
                  </Box>
                </Box>
              );
            },
            filterVariant: 'range',
          },
          {
            accessorFn: (row) => row.deleted ? 'Supprimé' : 'Actif',
            id: 'deleted',
            header: 'Status',
            size: 40,
            Cell: ({ row }) => (
              row.original.deleted
                ? <Chip label="Supprimé" color="error" size="small" />
                : <Chip label="Actif" color="primary" size="small" />
              //   ? <HighlightOffIcon color='error' />
              // : <CheckCircleIcon />
            ),
            filterVariant: 'autocomplete',
          },

          // {
          //   accessorFn: (row) =>
          //     row.mailboxStorageLimit
          //       ? (row.mailboxStorageLimit / (1024 ** 3)).toFixed(2)
          //       : 'N/A',
          //   id: 'mailboxStorageLimit',
          //   header: 'Quota Mailbox (Go)',
          //   size: 100,
          //   Cell: ({ renderedCellValue }) => (
          //     <Typography variant="body2">
          //       {renderedCellValue}
          //     </Typography>
          //   ),
          //   filterVariant: 'range',
          // },
        ]
      }
    ],
    [],
  );


  const table = useMaterialReactTable({
    enableColumnFilters: true,

    localization: MRT_Localization_FR,
    columns,
    data: filteredO365Users ? filteredO365Users : null, //data must be memoized or stable (useState, useMemo, defined outside of this component, etc.)
    enableColumnFilterModes: true,
    enableColumnOrdering: true,
    enableGrouping: true,
    enableGlobalFilter: true,
    enableColumnPinning: true,
    enableFacetedValues: true,
    // enableRowSelection: true,
    initialState: {
      density: "compact",
      showColumnFilters: true,
      showGlobalFilter: true,
      columnPinning: {
        left: ['mrt-row-expand', 'mrt-row-select'],
        right: ['mrt-row-actions'],
      },
      pagination: {
        pageSize: 50,

      }
    },
    paginationDisplayMode: 'pages',
    positionToolbarAlertBanner: 'bottom',
    enableRowSelection: true,
    muiSearchTextFieldProps: {
      size: 'small',
      variant: 'outlined',
    },
    muiPaginationProps: {
      color: 'secondary',
      rowsPerPageOptions: [50, 100, 150],
      shape: 'rounded',
      variant: 'outlined',
    },
    renderDetailPanel: ({ row }) => (
      <Box
        sx={{
          alignItems: 'center',
          display: 'flex',
          justifyContent: 'space-around',
          left: '30px',
          maxWidth: '1000px',
          position: 'sticky',
          width: '100%',
        }}
      >
        <Box sx={{ textAlign: 'center', width: '100%' }}>
          <DataGrid
            rows={row.original.assignedLicenses.map((license, index) => ({
              id: index,
              name: license.skuLicense,

            }))}
            disableColumnResize
            density="compact"
            getRowClassName={(params) =>
              params.indexRelativeToCurrentPage % 2 === 0 ? 'even' : 'odd'
            }
            columns={[
              { field: 'name', headerName: 'Nom', flex: 1 },
            ]}
            pageSize={5}
            rowsPerPageOptions={[5]}
            slotProps={{
              filterPanel: {
                filterFormProps: {
                  logicOperatorInputProps: {
                    variant: 'outlined',
                    size: 'small',
                  },
                  columnInputProps: {
                    variant: 'outlined',
                    size: 'small',
                    sx: { mt: 'auto' },
                  },
                  operatorInputProps: {
                    variant: 'outlined',
                    size: 'small',
                    sx: { mt: 'auto' },
                  },
                  valueInputProps: {
                    InputComponentProps: {
                      variant: 'outlined',
                      size: 'small',
                    },
                  },
                },
              },
            }}
          />
        </Box>
      </Box>
    ),
    renderToolbarInternalActions: ({ table }) => (

      <Box

      >
        <IconButton
          id="basic-button"
          aria-controls={open ? 'basic-menu' : undefined}
          aria-haspopup="true"
          aria-expanded={open ? 'true' : undefined}
          onClick={handleClick}
        >
          <FileDownloadIcon />
        </IconButton>
        <Menu
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
        >
          <MenuItem
            disabled={table.getRowModel().rows.length === 0}
            onClick={() => handleExportRows(table.getRowModel().rows)}
            startIcon={<FileDownloadIcon />}>
            Exporter la page actuelle
          </MenuItem>
          <MenuItem
            disabled={table.getPrePaginationRowModel().rows.length === 0}
            onClick={() =>
              handleExportRows(table.getPrePaginationRowModel().rows)
            }
            startIcon={<FileDownloadIcon />}>
            Exporter le filtre actuel
          </MenuItem>
          <MenuItem
            disabled={
              !table.getIsSomeRowsSelected() && !table.getIsAllRowsSelected()
            }
            //only export selected rows
            onClick={() => handleExportRows(table.getSelectedRowModel().rows)}
            startIcon={<FileDownloadIcon />}
          >
            Exporter les lignes sélectionnées
          </MenuItem>
        </Menu>
        <MRT_ToggleDensePaddingButton table={table} />
        <MRT_ToggleFullScreenButton table={table} />
        <MRT_ToggleFiltersButton table={table} />
        <MRT_ToggleGlobalFilterButton table={table} />
      </Box>
    ),
  });


  return (
    <Box>
      <PageHeader section="O365" title="Utilisateurs" sx={{ my: 1, mb: 3 }} />

      {loading ? (
        <Skeleton variant="rectangular" width="100%" height={600} />
      ) : o365Users.length === 0 ? (
        <Box sx={{ textAlign: 'center', p: 2 }}>
          <Typography variant="h6" color="textSecondary">
            Aucune donnée disponible
          </Typography>
        </Box>
      ) : (
        <MaterialReactTable table={table} />
      )}
    </Box>
  );


};

export default O365Users;
